import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { ResultActions } from "@/components/ResultActions";
import { UpgradeBanner } from "@/components/UpgradeBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Video, Loader2, Lightbulb, Crown, Image } from "lucide-react";

const CATEGORIES = ["Killer", "Survivor", "Funny/Meme", "Challenge", "Educational"];

export default function VideoIdeaGenerator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState("Killer");
  const [loading, setLoading] = useState(false);
  const [idea, setIdea] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [version, setVersion] = useState(0);
  const [personalized, setPersonalized] = useState(false);
  const [channelStyle, setChannelStyle] = useState("");
  const isPro = user?.is_pro;

  const generate = async () => {
    if (!user) return setAuthOpen(true);
    setLoading(true);
    setIdea(null);
    try {
      const { data } = await api.post("/generate/video-idea", {
        category,
        personalized: isPro && personalized,
        channel_style: channelStyle,
      });
      setIdea(data);
      setVersion((v) => v + 1);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data.detail, { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      } else {
        toast.error("Generation failed, please retry.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="video-gen-page" className="mx-auto max-w-5xl px-6 py-14 md:px-10">
      <div className="mb-3 flex items-center gap-2 text-primary">
        <Video className="h-5 w-5" />
        <span className="text-xs font-bold uppercase tracking-[0.25em]">Video Idea Generator</span>
      </div>
      <h1 className="font-display text-5xl tracking-wider md:text-6xl">Never run out of <span className="text-primary">content</span></h1>
      <p className="mt-3 max-w-2xl text-foreground/60">Pick a category and let the Entity conjure your next viral Dead by Daylight video.</p>

      <div className="mt-8"><UpgradeBanner version={version} /></div>

      <div className="border border-border bg-card p-6 md:p-8">
        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-foreground/70">Category</p>
        <div className="flex flex-wrap gap-2.5">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`category-${c.replace(/\W/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`border px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                category === c ? "border-primary bg-primary text-primary-foreground" : "border-white/10 bg-transparent text-foreground/70 hover:border-primary/50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className={`mt-6 border p-4 ${personalized && isPro ? "border-primary/40 bg-primary/5" : "border-white/10"}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-primary" />
              <span className="text-xs font-bold uppercase tracking-widest text-foreground/80">Personalized to your channel</span>
              <span className="border border-primary/40 bg-primary/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary">Pro</span>
            </div>
            <button
              data-testid="personalized-toggle"
              onClick={() => isPro ? setPersonalized((v) => !v) : navigate("/pricing")}
              className={`relative h-6 w-11 rounded-full transition-colors ${personalized && isPro ? "bg-primary" : "bg-white/15"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${personalized && isPro ? "left-5" : "left-0.5"}`} />
            </button>
          </div>
          {isPro && personalized && (
            <Input
              data-testid="channel-style-input"
              value={channelStyle}
              onChange={(e) => setChannelStyle(e.target.value)}
              placeholder="Describe your channel style, e.g. 'high-energy edits, comedy focus, beginner audience'"
              className="mt-3 rounded-sm border-white/10 bg-background"
            />
          )}
          {!isPro && <p className="mt-2 text-xs text-foreground/40">Upgrade to Pro to tailor ideas to your exact channel style and audience.</p>}
        </div>

        <Button data-testid="generate-video-btn" onClick={generate} disabled={loading} className="mt-7 w-full rounded-sm bg-primary py-6 text-sm font-bold uppercase tracking-widest hover:bg-primary/90 md:w-auto md:px-10">
          {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Conjuring…</> : <><Lightbulb className="mr-2 h-5 w-5" /> Generate Idea</>}
        </Button>
      </div>

      {idea && (
        <div data-testid="video-result" className="fade-up mt-6 border border-primary/30 bg-card p-6 md:p-8">
          <span className="inline-block border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">{idea.category}</span>
          {idea.personalized && <span className="ml-2 inline-flex items-center gap-1 border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary"><Crown className="h-3 w-3" /> Personalized</span>}
          <h2 className="mt-4 font-display text-3xl leading-tight tracking-wide md:text-4xl">{idea.title}</h2>
          {idea.hook && <p className="mt-3 text-lg italic text-primary/90">“{idea.hook}”</p>}
          <p className="mt-4 text-foreground/70">{idea.description}</p>
          {idea.thumbnail && (
            <div className="mt-4 flex items-start gap-2 border-l-2 border-primary bg-background/40 p-3">
              <Image className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p className="text-sm text-foreground/70"><span className="font-bold text-foreground/80">Thumbnail: </span>{idea.thumbnail}</p>
            </div>
          )}
          {Array.isArray(idea.tips) && (
            <div className="mt-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-foreground/60">Production Tips</p>
              <ul className="space-y-2">
                {idea.tips.map((t) => (
                  <li key={t} className="flex gap-3 text-sm text-foreground/70">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 bg-primary" />{t}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-7 border-t border-white/10 pt-5">
            <ResultActions kind="video" data={idea} onRequireAuth={() => setAuthOpen(true)} />
          </div>
        </div>
      )}

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
